// Pure geometry + formatting helpers for the Loss Exceedance chart.
// Kept DOM-free so the math is unit-tested independently of the SVG render.
import type { ExceedancePoint } from './types'

export type Geom = {
  width: number
  height: number
  mL: number
  mR: number
  mT: number
  mB: number
  /** Domain max on the x-axis, in dollars. */
  xMax: number
}

export const DEFAULT_MARGINS = { mL: 58, mR: 24, mT: 26, mB: 52 } as const

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

export function plotWidth(g: Geom): number {
  return g.width - g.mL - g.mR
}

export function plotHeight(g: Geom): number {
  return g.height - g.mT - g.mB
}

/** Map a dollar limit (0..xMax) to an x pixel. Clamps out-of-domain values. */
export function scaleX(g: Geom, limit: number): number {
  const span = g.xMax > 0 ? g.xMax : 1
  return g.mL + (clamp(limit, 0, span) / span) * plotWidth(g)
}

/** Map a probability (0..1) to a y pixel. p=1 -> top, p=0 -> baseline. */
export function scaleY(g: Geom, prob: number): number {
  return g.mT + (1 - clamp(prob, 0, 1)) * plotHeight(g)
}

/**
 * Domain max in dollars: the largest of the curve's limits and any provided
 * marker limits (so p99 etc. never falls off the right edge). Never returns 0.
 */
export function computeDomainMax(
  curve: ExceedancePoint[] | null | undefined,
  ...marks: Array<number | null | undefined>
): number {
  const vals: number[] = []
  if (curve) for (const p of curve) if (isFinite(p.limit)) vals.push(p.limit)
  for (const m of marks) if (typeof m === 'number' && isFinite(m)) vals.push(m)
  const max = vals.length ? Math.max(...vals) : 0
  return max > 0 ? max : 1
}

/** SVG path `d` for the exceedance (survival) curve. Empty string when no data. */
export function buildExceedancePath(
  curve: ExceedancePoint[] | null | undefined,
  g: Geom
): string {
  if (!curve || curve.length === 0) return ''
  return curve
    .map(
      (pt, i) =>
        `${i === 0 ? 'M' : 'L'}${scaleX(g, pt.limit).toFixed(1)} ${scaleY(g, pt.prob).toFixed(1)}`
    )
    .join(' ')
}

/** Compact money: $5M, $16.4M, $30M, $1.2B, $0. Null/NaN -> em dash. */
export function formatMoneyShort(v: number | null | undefined): string {
  if (v == null || !isFinite(v)) return '—'
  if (v === 0) return '$0'
  const sign = v < 0 ? '-' : ''
  const abs = Math.abs(v)
  const pick = (n: number, suffix: string) => {
    const r = Math.round(n * 10) / 10
    const s = r % 1 === 0 ? String(r) : r.toFixed(1)
    return `${sign}$${s}${suffix}`
  }
  if (abs >= 1e9) return pick(abs / 1e9, 'B')
  if (abs >= 1e6) return pick(abs / 1e6, 'M')
  if (abs >= 1e3) return pick(abs / 1e3, 'K')
  return `${sign}$${Math.round(abs)}`
}

/** Percent label from a 0..1 probability: 0.22 -> "22%". Null/NaN -> em dash. */
export function formatPct(p: number | null | undefined): string {
  if (p == null || !isFinite(p)) return '—'
  return `${Math.round(p * 100)}%`
}

/** N+1 evenly spaced x tick values across [0, xMax]. Deterministic, no nice-rounding. */
export function xTicks(xMax: number, count = 6): number[] {
  const out: number[] = []
  for (let i = 0; i <= count; i++) out.push((xMax * i) / count)
  return out
}
